import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CustomerTimelineItemDto, FollowUpDto, OrderStatus } from '@ecom/contracts';
import { FollowUpSheet } from '@/components/follow-up-sheet';
import { LoadMore, Section } from '@/components/page';
import { Button, EmptyState, Pill, cx, type PillTone } from '@/components/ui';
import { useUiStore } from '@/i18n';
import type { MessageKey } from '@/i18n/messages';
import { useStatusLabel, useT } from '@/i18n/use-t';
import { dateTime, money } from '@/lib/format';
import { statusTone } from '@/lib/order-status';
import { returnStatusKey, returnStatusTone } from '@/lib/returns';
import { useFollowUpsStore } from '@/stores/follow-ups.store';

/**
 * The profile's CRM (ADR-104): their open follow-ups, and everything that has
 * happened with them in one feed. Both read from the follow-ups store, which
 * the profile points at this customer when it opens.
 */

/** Open follow-ups for this customer, the earliest due first, each done with one press. */
export function CustomerFollowUps({
  customer,
  orders,
  mayWrite,
}: {
  customer: { id: string; name: string };
  orders: { id: string; number: string }[];
  mayWrite: boolean;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { customerItems, done } = useFollowUpsStore();
  const [editing, setEditing] = useState<FollowUpDto | 'new' | null>(null);

  return (
    <Section
      title={t('followUps')}
      subtitle={t('followUpsCustomerHint')}
      flush
      action={
        mayWrite ? (
          <Button variant="ghost" size="sm" onClick={() => setEditing('new')}>
            {t('followUpAdd')}
          </Button>
        ) : undefined
      }
    >
      {customerItems.length === 0 ? (
        <p className="border-t border-line px-5 py-4 text-sm text-ink-muted">{t('followUpsEmpty')}</p>
      ) : (
        <ul className="divide-y divide-line border-t border-line">
          {customerItems.map((row) => (
            <li key={row.id} className="flex items-start gap-3 px-5 py-3">
              <button
                type="button"
                className="min-w-0 flex-1 text-start"
                disabled={!mayWrite}
                onClick={() => setEditing(row)}
              >
                <span dir="auto" className="block text-sm font-medium text-ink">{row.title}</span>
                <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                  <span>{dateTime(row.dueAt, locale)}</span>
                  {row.overdue ? <Pill tone="danger">{t('followUpOverdue')}</Pill> : null}
                  <span>· {row.assignee?.name ?? t('followUpNobody')}</span>
                  {row.order ? <span dir="ltr">· {row.order.number}</span> : null}
                </span>
              </button>
              {mayWrite ? (
                <Button variant="secondary" size="sm" onClick={() => void done(row.id)}>
                  {t('followUpMarkDone')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <FollowUpSheet followUp={editing === 'new' ? null : editing} customer={customer} orders={orders} onClose={() => setEditing(null)} />
      ) : null}
    </Section>
  );
}

const KIND_DOTS: Record<CustomerTimelineItemDto['kind'], string> = {
  joined: 'bg-ink-muted',
  order: 'bg-brand-600',
  return: 'bg-warning',
  ticket: 'bg-accent-sky-ink',
  review: 'bg-accent-amber-ink',
  note: 'bg-accent-violet-ink',
  message: 'bg-ink-faint',
  follow_up: 'bg-success',
};

/** Everything with them, newest first — read in pages from the server. */
export function CustomerTimeline() {
  const t = useT();
  const { timeline, timelineCursor, timelineLoading, loadMoreTimeline } = useFollowUpsStore();

  return (
    <Section title={t('customerTimeline')} subtitle={t('timelineHint')} flush>
      {timeline.length === 0 && !timelineLoading ? (
        <div className="border-t border-line">
          <EmptyState title={t('timelineEmpty')} />
        </div>
      ) : (
        <ol className="border-t border-line px-5 py-4">
          {timeline.map((item, index) => (
            <li key={item.id} className="relative flex gap-3 pb-4 last:pb-0">
              {index < timeline.length - 1 ? <span aria-hidden className="absolute start-[5px] top-4 h-full w-px bg-line" /> : null}
              <span aria-hidden className={cx('relative mt-1.5 size-[11px] shrink-0 rounded-full ring-2 ring-surface', KIND_DOTS[item.kind])} />
              <TimelineEntry item={item} />
            </li>
          ))}
        </ol>
      )}
      <LoadMore visible={Boolean(timelineCursor)} loading={timelineLoading} onClick={() => void loadMoreTimeline()} />
    </Section>
  );
}

function TimelineEntry({ item }: { item: CustomerTimelineItemDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const statusLabel = useStatusLabel();
  const pills: { tone: PillTone; label: string }[] = [];
  let head: ReactNode;
  let to: string | null = null;

  switch (item.kind) {
    case 'joined':
      head = t('timelineJoined');
      break;
    case 'order':
      head = t('timelineOrder', { number: item.label ?? '' });
      to = `/orders/${item.refId}`;
      if (item.status) pills.push({ tone: statusTone(item.status as OrderStatus), label: statusLabel(item.status) });
      break;
    case 'return':
      head = t('timelineReturn', { number: item.label ?? '' });
      to = `/returns?open=${item.refId}`;
      if (item.status) pills.push({ tone: returnStatusTone(item.status) as PillTone, label: t(returnStatusKey(item.status)) });
      break;
    case 'ticket':
      head = t('timelineTicket', { number: item.label ?? '' });
      to = '/support';
      if (item.status) pills.push({ tone: item.status === 'OPEN' ? 'warning' : 'neutral', label: t(`ticketStatus${item.status}` as MessageKey) });
      break;
    case 'review':
      head = t('timelineReview', { product: item.label ?? '' });
      to = '/reviews';
      if (item.status) {
        pills.push(
          item.status === 'APPROVED'
            ? { tone: 'success', label: t('approved') }
            : item.status === 'REJECTED'
              ? { tone: 'danger', label: t('rejected') }
              : { tone: 'warning', label: t('pendingQueue') },
        );
      }
      break;
    case 'note':
      head = t('timelineNote', { name: item.actor ?? '' });
      break;
    case 'follow_up':
      head = t('timelineFollowUp', { name: item.actor ?? '' });
      to = '/follow-ups?view=everyone';
      pills.push(item.status === 'done' ? { tone: 'success', label: t('followUpsDone') } : { tone: 'info', label: t('followUpOpen') });
      break;
    case 'message':
      // A WhatsApp update names itself ("WhatsApp: order on its way", ADR-105); a mail or text is "Message: …".
      head = item.channel === 'WHATSAPP' && item.title ? item.title[locale] : t('timelineMessage', { name: item.title?.[locale] ?? item.text ?? '' });
      if (item.status === 'FAILED') pills.push({ tone: 'danger', label: t('timelineFailed') });
      if (item.opened) pills.push({ tone: 'neutral', label: item.channel === 'WHATSAPP' ? t('whatsappRead') : t('timelineOpened') });
      if (item.clicked) pills.push({ tone: 'success', label: t('timelineClicked') });
      break;
  }

  // A message's subject repeats its name when the template's own words are used: shown only when it says more.
  const showText = Boolean(item.text) && !(item.kind === 'message' && item.title && item.text === item.title[locale]);
  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        {to ? (
          <Link to={to} className="text-sm font-medium text-ink hover:text-brand-700 hover:underline">
            {head}
          </Link>
        ) : (
          <span className="text-sm font-medium text-ink">{head}</span>
        )}
        {item.rating ? <span className="text-xs text-accent-amber-ink">{'★'.repeat(item.rating)}</span> : null}
        {item.amount !== null ? (
          <span data-numeric className="text-xs text-ink-muted">
            {money(item.amount, locale)}
          </span>
        ) : null}
        {pills.map((pill) => (
          <Pill key={pill.label} tone={pill.tone}>
            {pill.label}
          </Pill>
        ))}
      </div>
      {showText ? (
        <p dir="auto" className="mt-1 line-clamp-3 whitespace-pre-line text-start text-sm text-ink-muted">
          {item.text}
        </p>
      ) : null}
      <p className="mt-1 text-xs text-ink-faint">{dateTime(item.at, locale)}</p>
    </div>
  );
}
